import { DatasetType, type ResourceOutput } from '@cotera/api';
import { fromIsoString, toIsoString } from '../chat/iso-date';

export { DatasetType };
export type DatasetColumnValue = string | number | boolean | Date | null;
export type DatasetRow = Record<string, DatasetColumnValue>;

export type DatasetColumnSchema = {
  readonly name: string;
  readonly type: string | null;
};

export type DatasetSchema = {
  readonly columns: readonly DatasetColumnSchema[];
};

export type DatasetMetadata = {
  readonly rowCount: number | null;
  readonly estimatedSizeBytes: number | null;
  readonly schema: DatasetSchema;
};

export type DatasetSampleArtifact = {
  readonly datasetId: string;
  readonly rows: readonly DatasetRow[];
  readonly metadata: DatasetMetadata;
};

export type DatasetArtifactMetadata = {
  readonly rowCount: number | null;
  /**
   * Rows the artifact was taken from, when the writer recorded it. Greater than
   * `rowCount` means the artifact is a sample of something larger — which
   * nothing in the file itself reveals. Absent on artifacts written before it
   * was recorded.
   */
  readonly sourceRowCount?: number | null;
  readonly estimatedSizeBytes: number | null;
  readonly schema: DatasetSchema;
};

export type DatasetArtifactData = {
  readonly id: string;
  readonly datasetId: string;
  readonly pointer: string;
  readonly hash: string;
  readonly metadata?: DatasetArtifactMetadata | null;
  readonly expiresAt: Date | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly lastAccessedAt: Date | null;
};

export type SerializedDatasetArtifact = {
  readonly id: string;
  readonly datasetId: string;
  readonly pointer: string;
  readonly hash: string;
  readonly metadata?: DatasetArtifactMetadata | null;
  readonly expiresAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lastAccessedAt: string | null;
};

export type DatasetData = {
  readonly id: string;
  readonly orgId: string;
  readonly ownerId: string;
  readonly type: DatasetType;
  readonly name: string;
  readonly originalFilename: string | null;
  readonly mimeType: string | null;
  readonly sizeBytes: number | null;
  readonly status: 'active' | 'archived';
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly folderId?: string | null;
  readonly artifacts: readonly DatasetArtifactData[];
};

export type SerializedDataset = {
  readonly id: string;
  readonly orgId: string;
  readonly ownerId: string;
  readonly type: DatasetType;
  readonly name: string;
  readonly originalFilename: string | null;
  readonly mimeType: string | null;
  readonly sizeBytes: number | null;
  readonly status: 'active' | 'archived';
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly folderId?: string | null;
  readonly artifacts?: readonly SerializedDatasetArtifact[];
};

export type DatasetResourceOutput = Extract<
  ResourceOutput,
  { type: 'dataset' }
>;

function toDatasetArtifactData(
  serialized: SerializedDatasetArtifact
): DatasetArtifactData {
  return {
    id: serialized.id,
    datasetId: serialized.datasetId,
    pointer: serialized.pointer,
    hash: serialized.hash,
    metadata: serialized.metadata,
    expiresAt:
      serialized.expiresAt !== null
        ? fromIsoString(serialized.expiresAt)
        : null,
    createdAt: fromIsoString(serialized.createdAt),
    updatedAt: fromIsoString(serialized.updatedAt),
    lastAccessedAt:
      serialized.lastAccessedAt !== null
        ? fromIsoString(serialized.lastAccessedAt)
        : null,
  };
}

function toDatasetData(serialized: SerializedDataset): DatasetData {
  return {
    id: serialized.id,
    orgId: serialized.orgId,
    ownerId: serialized.ownerId,
    type: serialized.type,
    name: serialized.name,
    originalFilename: serialized.originalFilename,
    mimeType: serialized.mimeType,
    sizeBytes: serialized.sizeBytes,
    status: serialized.status,
    createdAt: fromIsoString(serialized.createdAt),
    updatedAt: fromIsoString(serialized.updatedAt),
    folderId: serialized.folderId,
    artifacts: (serialized.artifacts ?? []).map(toDatasetArtifactData),
  };
}

export class DatasetArtifact {
  private readonly _data: DatasetArtifactData;

  private constructor(data: DatasetArtifactData) {
    this._data = data;
  }

  get id(): string {
    return this._data.id;
  }

  get datasetId(): string {
    return this._data.datasetId;
  }

  get pointer(): string {
    return this._data.pointer;
  }

  get hash(): string {
    return this._data.hash;
  }

  get metadata(): DatasetArtifactMetadata | null {
    return this._data.metadata ?? null;
  }

  get expiresAt(): Date | null {
    return this._data.expiresAt;
  }

  get lastAccessedAt(): Date | null {
    return this._data.lastAccessedAt;
  }

  isExpired(now: Date): boolean {
    return this._data.expiresAt !== null && this._data.expiresAt <= now;
  }

  wasAccessedWithin(now: Date, windowMs: number): boolean {
    if (this._data.lastAccessedAt === null) {
      return false;
    }
    return now.getTime() - this._data.lastAccessedAt.getTime() <= windowMs;
  }

  serialize(): SerializedDatasetArtifact {
    return {
      id: this._data.id,
      datasetId: this._data.datasetId,
      pointer: this._data.pointer,
      hash: this._data.hash,
      metadata: this._data.metadata,
      expiresAt:
        this._data.expiresAt !== null
          ? toIsoString(this._data.expiresAt)
          : null,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
      lastAccessedAt:
        this._data.lastAccessedAt !== null
          ? toIsoString(this._data.lastAccessedAt)
          : null,
    };
  }

  static fromSerialized(
    serialized: SerializedDatasetArtifact
  ): DatasetArtifact {
    return new DatasetArtifact(toDatasetArtifactData(serialized));
  }
}

export class Dataset {
  private readonly _data: DatasetData;
  private readonly _artifacts: readonly DatasetArtifact[];

  private constructor(data: DatasetData) {
    this._data = data;
    this._artifacts = data.artifacts.map((artifact) =>
      DatasetArtifact.fromSerialized({
        id: artifact.id,
        datasetId: artifact.datasetId,
        pointer: artifact.pointer,
        hash: artifact.hash,
        metadata: artifact.metadata,
        expiresAt:
          artifact.expiresAt !== null ? toIsoString(artifact.expiresAt) : null,
        createdAt: toIsoString(artifact.createdAt),
        updatedAt: toIsoString(artifact.updatedAt),
        lastAccessedAt:
          artifact.lastAccessedAt !== null
            ? toIsoString(artifact.lastAccessedAt)
            : null,
      })
    );
  }

  get id(): string {
    return this._data.id;
  }

  get orgId(): string {
    return this._data.orgId;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get type(): DatasetType {
    return this._data.type;
  }

  get name(): string {
    return this._data.name;
  }

  get status(): 'active' | 'archived' {
    return this._data.status;
  }

  get folderId(): string | null | undefined {
    return this._data.folderId;
  }

  get artifacts(): readonly DatasetArtifact[] {
    return this._artifacts;
  }

  displayName(): string {
    return this._data.name;
  }

  currentArtifact(): DatasetArtifact | null {
    return this._artifacts[0] ?? null;
  }

  artifactsAccessedRecently(params: {
    readonly now: Date;
    readonly windowMs: number;
  }): readonly DatasetArtifact[] {
    return this._artifacts.filter((artifact) =>
      artifact.wasAccessedWithin(params.now, params.windowMs)
    );
  }

  serialize(): SerializedDataset {
    const base: SerializedDataset = {
      id: this._data.id,
      orgId: this._data.orgId,
      ownerId: this._data.ownerId,
      type: this._data.type,
      name: this._data.name,
      originalFilename: this._data.originalFilename,
      mimeType: this._data.mimeType,
      sizeBytes: this._data.sizeBytes,
      status: this._data.status,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
      artifacts: this._artifacts.map((artifact) => artifact.serialize()),
    };
    if (this._data.folderId !== undefined) {
      return { ...base, folderId: this._data.folderId };
    }
    return base;
  }

  static fromSerialized(serialized: SerializedDataset): Dataset {
    return new Dataset(toDatasetData(serialized));
  }

  static fromResourceOutput(output: DatasetResourceOutput): Dataset {
    const dataset = output.dataset;
    return Dataset.fromSerialized({
      id: dataset.id,
      orgId: dataset.orgId,
      ownerId: dataset.ownerId,
      type: dataset.type,
      name: dataset.name,
      originalFilename: dataset.originalFilename,
      mimeType: dataset.mimeType,
      sizeBytes: dataset.sizeBytes,
      status: dataset.status,
      createdAt: toIsoString(dataset.createdAt),
      updatedAt: toIsoString(dataset.updatedAt),
      folderId: output.meta?.folderId ?? null,
      artifacts: (dataset.artifacts ?? []).map((artifact) => ({
        id: artifact.id,
        datasetId: artifact.datasetId,
        pointer: artifact.pointer,
        hash: artifact.hash,
        metadata: artifact.metadata,
        expiresAt:
          artifact.expiresAt !== null ? toIsoString(artifact.expiresAt) : null,
        createdAt: toIsoString(artifact.createdAt),
        updatedAt: toIsoString(artifact.updatedAt),
        lastAccessedAt:
          artifact.lastAccessedAt !== null
            ? toIsoString(artifact.lastAccessedAt)
            : null,
      })),
    });
  }
}
